<template>
    <div>
        <div class="smartform-inline-layout">
                <v-form lazy-validation v-model="formValid" class="smartform-inline-form">
                    <div class="smartform-inline-forms-row">
                        <div class="smartform-inline-forms-wrap">
                            <fe-remote-combo
                                label="Forms"
                                v-model="panelValues.forms"
                                :items="items"
                                item-value="id"
                                item-text="name"
                                multiple
                                clearable
                                return-object
                                class="smartform-inline-forms-combo"
                                data-test="smartform-forms-filter-field"
                            />
                        </div>
                        <v-switch v-model="panelValues.restrict" label="Restrict Results" hide-details :ripple="false" class="smartform-inline-toggle" data-test="smartform-restrict-results-switch" />
                    </div>
                    <div class="smartform-inline-year-row">
                        <fe-remote-combo
                            label="Created/Updated"
                            v-model="panelValues.year_type"
                            :clearable="false"
                            :items="[
                                {id:'created', name:'Created'},
                                {id:'updated', name:'Updated'},
                            ]"
                            item-value="id"
                            item-text="name"
                            class="smartform-inline-year-field"
                            data-test="smartform-created-updated-filter-field"
                        />
                        <fe-remote-combo
                            label="School Year"
                            v-model="panelValues.school_year"
                            rootProperty="years"
                            url="qry.php?action=get&property=school_years"
                            class="smartform-inline-year-field"
                            :error="schoolYearRequiredError"
                            :error-messages="schoolYearRequiredError ? 'School Year is required.' : ''"
                            data-test="smartform-school-year-filter-field"
                        />
                        <v-switch v-model="panelValues.require_all" label="Require All" hide-details :ripple="false" class="smartform-inline-toggle" data-test="smartform-require-all-switch" />
                    </div>
                </v-form>
            </div>
    </div>
</template>

<script>
import * as Util from '../../Util'
import FilterUtil from '../../FilterPanelUtil'

export default {
    name: 'SmartformPanel',

    props: {
        existingFilter: { type: Object, default: null },
    },

    rank: 1,

    data() {
        return {
            formValid: true,
            displayName: 'smartFORM',
            type: 'form',
            items: [],
            panelValues: {
                restrict: true,
                year_type: {id:'created'},
                require_all: false,
                school_year: null,
                forms: [],
            },
        }
    },

    watch: {
        existingFilter: {
            handler(f) {
                // Sync form state from the applied filter when it changes; clear form when the filter is removed.
                if (f?.editorParams) {
                    const ep = f.editorParams
                    this.panelValues = {
                        restrict: ep.restrict,
                        year_type: ep.year_type || { id: 'created' },
                        require_all: ep.require_all,
                        school_year: ep.school_year ? { ...ep.school_year } : null,
                        forms: ep.forms ? [...ep.forms] : [],
                    }
                } else {
                    this.panelValues = {
                        restrict: true,
                        year_type: { id: 'created' },
                        require_all: false,
                        school_year: null,
                        forms: [],
                    }
                    this.$nextTick(() => this.loadDefaultSchoolYear())
                }
            },
            immediate: true,
        },
        inlineValid: {
            handler(v) {
                this.$emit('valid-change', v)
            },
            immediate: true,
        },
        schoolYearRequiredError: {
            handler(v) {
                this.$emit('invalid-change', v)
            },
            immediate: true,
        },
        isDirty: {
            handler(v) {
                this.$emit('dirty-change', v)
            },
            immediate: true,
        },
    },

    computed: {
        schoolYearRequiredError() {
            const forms = this.panelValues.forms
            const schoolYear = this.panelValues.school_year
            const hasForms = Array.isArray(forms) && forms.length > 0
            return hasForms && !schoolYear
        },
        inlineValid() {
            const forms = this.panelValues.forms
            const schoolYear = this.panelValues.school_year
            const hasForms = Array.isArray(forms) && forms.length > 0
            return hasForms && !!schoolYear
        },
        isDirty() {
            // Monitor form state to enable/disable apply button appropriately
            const applied = this.existingFilter?.editorParams
            if (!applied) return false
            const current = this.panelValues

            if (applied.restrict !== current.restrict || applied.require_all !== current.require_all) return true

            const appliedYearTypeId = applied.year_type?.id ?? null
            const currentYearTypeId = current.year_type?.id ?? null
            if (appliedYearTypeId !== currentYearTypeId) return true

            const appliedSchoolYearId = applied.school_year?.id ?? null
            const currentSchoolYearId = current.school_year?.id ?? null
            if (appliedSchoolYearId !== currentSchoolYearId) return true

            const appliedIds = !Array.isArray(applied.forms) ? [] : applied.forms.map(x => x?.id ?? x).sort((a, b) => a - b)
            const currentIds = !Array.isArray(current.forms) ? [] : current.forms.map(x => x?.id ?? x).sort((a, b) => a - b)
            return appliedIds.length !== currentIds.length || appliedIds.some((id, i) => id !== currentIds[i])
        },
    },

    mounted() {
        this.loadDefaultSchoolYear()
        this.loadForms()
        this.$emit('valid-change', this.inlineValid)
    },

    methods: {
        loadDefaultSchoolYear() {
            this.$axios.get('qry.php?action=get&property=school_years').then((r) => {
                const years = r.data?.years || []
                if (years.length && !this.panelValues.school_year) {
                    this.$set(this.panelValues, 'school_year', years[0])
                }
            })
        },
        getFilter() {
            if (this.$_.isEmpty(this.panelValues.forms)) return null
            const uniqueId = Util.UUIDv4()
            const name = this.panelValues.forms.map(x => x.name).join(', ')
            const filter = {
                type: this.type,
                id: uniqueId,
                restrict: this.panelValues.restrict,
                report: true,
                params: this.$_.pickBy({
                    school_year_id: this.panelValues.school_year?.id,
                    year_type: this.panelValues.year_type?.id,
                    form_id: this.panelValues.forms.map(x => x.id),
                    require_all: this.panelValues.require_all,
                }, x => !this.$_.isBlank(x)),
            }
            FilterUtil.attachFilterDescriptor(filter, {
                editor: this,
                editorParams: {
                    restrict: this.panelValues.restrict,
                    forms: this.panelValues.forms ? [...this.panelValues.forms] : null,
                    year_type: this.panelValues.year_type,
                    require_all: this.panelValues.require_all,
                    school_year: this.panelValues.school_year ? { ...this.panelValues.school_year } : null,
                },
                chipText: name,
                tooltipData: this.$_.pickBy({
                    'Forms': name,
                    'School Year By': this.panelValues.year_type?.name,
                    'School Year': this.panelValues.school_year?.name,
                    'Restrict': this.panelValues.restrict,
                    'Require All': this.panelValues.require_all,
                }, x => !this.$_.isBlank(x)),
                grouper: {
                    id: uniqueId,
                    name,
                    type: 'propertyArray',
                    args: { path: `filters.${uniqueId}`, property: 'value' },
                },
            })
            return filter
        },

        loadForms() {
            this.$axios({
                url: 'form.php?action=get&property=form&active=1&ignore_permissions=1',
            }).then((response) => {
                if (response && response.data) {
                    this.items = response.data.forms.sort(this.formSort)
                }
            })
        },
        formSort(a,b) {
            if(a.name.toLowerCase() < b.name.toLowerCase()) return -1
            if(a.name.toLowerCase() > b.name.toLowerCase()) return 1
            return 0
        },
    },
}
</script>

<style lang="scss" scoped>
.smartform-inline-layout {
    width: 100%;
    min-width: 0;
}
.smartform-inline-form {
    display: flex;
    flex-direction: column;
    gap: 16px 0;
}
.smartform-inline-forms-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0 24px;
    flex-wrap: wrap;
}
.smartform-inline-forms-wrap {
    width: 66%;
    min-width: 0;
}
.smartform-inline-toggle {
    flex: 0 0 auto;
}
.smartform-inline-year-row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px 24px;
    align-items: center;
}
.smartform-inline-year-field {
    flex: 0 1 200px;
    min-width: 0;
}
</style>
